import { useEffect, useState } from 'react';
import { aTexto, compararDominios, leerDominios } from './corporateDomains';
import { useGuardarConfiguracion } from './useGuardarConfiguracion';
import { ConfigCard, AvisoDeGuardado } from './ConfigCard';

/**
 * El catálogo de dominios corporativos.
 *
 * Quien tenga correo de uno de estos dominios entra por SSO y **no puede auto-registrarse**; quien
 * no, se registra. O sea que un dominio de más le cierra el auto-registro a su gente, y uno de menos
 * se lo abre. Por eso el flujo no es «valida y guarda» sino **valida, enseña lo que va a cambiar, y
 * luego guarda**, en dos pasos: el primer botón sólo pide confirmación.
 */
export function DomainsCard() {
  const { t, data, isLoading, isError, guardando, guardar, aviso, mensajeDeError } = useGuardarConfiguracion();

  // El texto que se está editando. Se separa de lo guardado porque el diff «lo que va a cambiar» se
  // calcula contra lo GUARDADO, no contra lo que había hace un carácter.
  const [texto, setTexto] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  // 🔴 El textarea arranca (y se resincroniza) desde el servidor, pero SOLO mientras nadie lo esté
  // editando (`texto === null`). Sin esa condición, cualquier refetch de react-query —al recuperar el
  // foco de la pestaña, por ejemplo— borraría lo que la persona lleva escrito a medias.
  const guardados = data?.corporateDomains;
  useEffect(() => {
    if (guardados && texto === null) setTexto(aTexto(guardados));
  }, [guardados, texto]);

  if (isLoading) return <p className="jobs-card-hint" role="status">{t('common.loading')}</p>;
  if (isError || !data) return <p className="jobs-alert jobs-alert--bad" role="alert">{t('jobs.sync.loadError')}</p>;

  const leidos = leerDominios(texto ?? '');
  const cambio = compararDominios(data.corporateDomains ?? [], leidos.dominios);
  const hayCambios = cambio.anadidos.length > 0 || cambio.quitados.length > 0;

  const guardarDominios = () => {
    setConfirmando(false);
    // Se suelta el borrador al terminar para que el textarea vuelva a seguir al servidor: así lo que
    // se ve es lo que quedó guardado y normalizado (minúsculas, sin repetidos), no lo que se tecleó.
    guardar({ corporateDomains: leidos.dominios }, t('jobs.sync.domainsSaved'), () => setTexto(null));
  };

  return (
    <ConfigCard
      icono="fa-at"
      titulo={t('jobs.sync.domains')}
      descripcion={t('jobs.sync.domainsDesc')}
      pie={
        <>
          <AvisoDeGuardado aviso={aviso} error={mensajeDeError} />
          {data.updatedBy && (
            <p className="jobs-card-meta">{t('jobs.sync.updatedBy')} {data.updatedBy}</p>
          )}
        </>
      }
    >
      <fieldset className="jobs-fieldset" disabled={guardando}>
        <label className="jobs-field-label" htmlFor="corporateDomains">
          {t('jobs.sync.domainsHint')}
        </label>
        <textarea
          id="corporateDomains"
          className="jobs-textarea"
          value={texto ?? ''}
          onChange={e => { setTexto(e.target.value); setConfirmando(false); }}
          rows={12}
          spellCheck={false}
          autoCapitalize="none"
        />

        <p className="jobs-card-meta">{t('jobs.sync.domainsCount', { n: leidos.dominios.length })}</p>

        {leidos.problemas.length > 0 && (
          <ul className="jobs-alert jobs-alert--bad jobs-problem-list" role="alert">
            {leidos.problemas.map((problema, i) => (
              <li key={`${problema.tipo}-${problema.valor}-${i}`}>
                <code>{problema.valor}</code>{' — '}
                {problema.tipo === 'formato'
                  ? t('jobs.sync.domainBadFormat')
                  : t('jobs.sync.domainDuplicate')}
              </li>
            ))}
          </ul>
        )}

        {/* 🔴 El diff, y es la protección que de verdad importa. Ninguna regla de formato detecta que
            a una lista pegada le falta una coma —`campofriofg.comcaroli.ro` es sintácticamente
            impecable—, pero verlo escrito en «se va a añadir», junto a los dos que desaparecieron, sí.
            Y quitar un dominio ABRE el auto-registro a toda su gente: eso no puede pasar de un clic. */}
        {hayCambios && (
          <div className="jobs-diff">
            {cambio.quitados.length > 0 && (
              <p>
                <strong>{t('jobs.sync.domainsRemoved')}:</strong>{' '}
                <code>{cambio.quitados.join(', ')}</code>
                <small>{t('jobs.sync.domainsRemovedWarning')}</small>
              </p>
            )}
            {cambio.anadidos.length > 0 && (
              <p>
                <strong>{t('jobs.sync.domainsAdded')}:</strong>{' '}
                <code>{cambio.anadidos.join(', ')}</code>
                <small>{t('jobs.sync.domainsAddedWarning')}</small>
              </p>
            )}
          </div>
        )}

        {/* Dos pasos a propósito: el primero enseña el cambio, el segundo lo confirma. Un solo botón
            convertiría un pegado con una errata en un cambio de acceso sin ninguna pausa. */}
        <div className="jobs-card-actions">
          {!confirmando ? (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!hayCambios || leidos.dominios.length === 0 || guardando}
              onClick={() => setConfirmando(true)}
            >
              {t('jobs.sync.domainsReview')}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn--primary btn--sm" onClick={guardarDominios} disabled={guardando}>
                {t('jobs.sync.domainsConfirm')}
              </button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => setConfirmando(false)} disabled={guardando}>
                {t('jobs.sync.domainsCancel')}
              </button>
            </>
          )}
        </div>

        {leidos.dominios.length === 0 && (texto ?? '').trim() !== '' && (
          <p className="jobs-alert jobs-alert--bad" role="alert">{t('jobs.sync.domainsAllInvalid')}</p>
        )}
      </fieldset>
    </ConfigCard>
  );
}
